import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useImperativeHandle, useReducer, useRef, useState, } from 'react';
import styled, { css } from 'styled-components';
import { useDisposeEffect } from 'toasterhea';
import { useBoundingClientRectEffect, } from '../../utils/popups';
import { useOnLocationLeave } from '../../utils/useOnLocationLeave';
export function AbstractPopup({ controllerRef, anchorRef, children, reject, resolve: _resolve, translateFn, ...props }) {
    const backdropElementRef = useRef(null);
    const [boxKey, invalidateBoxKey] = useReducer((x) => x + 1, 0);
    useImperativeHandle(controllerRef, () => ({
        refreshAnchors: () => {
            invalidateBoxKey();
        },
    }));
    useBoundingClientRectEffect({
        anchorRef,
        translateFn,
        onUpdate: ({ x, y, width, height }) => {
            const { current: root } = backdropElementRef;
            if (root) {
                root.style.setProperty('--Popup_AnchorX', `${x}px`);
                root.style.setProperty('--Popup_AnchorY', `${y}px`);
                root.style.setProperty('--Popup_AnchorWidth', `${width}px`);
                root.style.setProperty('--Popup_AnchorHeight', `${height}px`);
            }
        },
        deps: [boxKey],
    });
    const innerElementRef = useRef(null);
    const [phase, setPhase] = useState();
    useDisposeEffect((discard) => {
        const { current: innerElement } = innerElementRef;
        if (!innerElement) {
            return () => { };
        }
        setPhase('unmounted');
        const discardTimeoutId = window.setTimeout(() => {
            /**
             * If `transitionend` doesn't hit in time, or at all, we discard the popup
             * automatically after 1s.
             */
            discard();
        }, 1000);
        function onTransitionEnd(e) {
            if (e.target !== e.currentTarget) {
                /**
                 * Since the `transitionend` is hooked up to one of popup's inner-containers it
                 * catches all nested events, too. We have to filter nested hits out.
                 */
                return;
            }
            window.clearTimeout(discardTimeoutId);
            discard();
        }
        innerElement.addEventListener('transitionend', onTransitionEnd);
        return () => {
            window.clearTimeout(discardTimeoutId);
            innerElement.removeEventListener('transitionend', onTransitionEnd);
        };
    }, []);
    useEffect(() => {
        function onKeyDown(e) {
            if (e.key === 'Escape') {
                reject(DismissalReason.Escape);
            }
        }
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [reject]);
    useEffect(() => {
        function onMouseDown(e) {
            if (!(e.target instanceof Element) || !innerElementRef.current) {
                return;
            }
            if (innerElementRef.current.contains(e.target)) {
                return;
            }
            reject(DismissalReason.Backdrop);
        }
        window.addEventListener('mousedown', onMouseDown);
        return () => {
            window.removeEventListener('mousedown', onMouseDown);
        };
    }, [reject]);
    useOnLocationLeave(() => {
        reject(DismissalReason.Abandon);
    });
    useEffect(() => {
        setPhase('mounted');
    }, []);
    return (_jsx(AbstractPopupBackdrop, { ...props, "$phase": phase, ref: backdropElementRef, children: _jsx(AbstractPopupOuter, { children: _jsx(AbstractPopupInner, { ref: innerElementRef, children: _jsx(AbstractPopupContent, { className: "tv-ui-shiny-border", children: children }) }) }) }));
}
const AbstractPopupContent = styled.div `
    --Popup_DefaultBackground: rgba(
        var(--Popup_BackgroundRGB, 202, 202, 202),
        var(--Popup_BackgroundOpacity, 0.4)
    );

    --Popup_DefaultBorder: 0.0625rem solid rgba(222, 222, 222, 0.2);

    --Popup_DefaultBoxShadow: 0px 0.75rem 1.5rem rgba(0, 0, 0, 0.3),
        inset 0px 0px 0.125rem rgba(255, 255, 255, 0.8);

    --tv-ui-shiny-border-radius: var(--Popup_BorderRadius);

    /**
     * Apply backdrop-filter to the element whose opacity is
     * animated — otherwise, the filter won't update smoothly
     * during the transition.
     */
    backdrop-filter: var(--Popup_BackdropFilter, blur(30px));
    -webkit-backdrop-filter: var(--Popup_BackdropFilter, blur(30px));
    background: var(--Popup_Background, var(--Popup_DefaultBackground));
    border: var(--Popup_Border, var(--Popup_DefaultBorder));
    border-radius: var(--Popup_BorderRadius, 0);
    box-shadow: var(--Popup_BoxShadow, var(--Popup_DefaultBoxShadow));
    box-sizing: border-box;
    color: var(--Popup_Color, #ffffff);
    height: var(--Popup_Height, auto);
    max-height: var(--Popup_MaxHeight, none);
    max-width: var(--Popup_MaxWidth, none);
    opacity: var(--Popup_InitialOpacity, 0);
    overflow: hidden;
    padding: var(--Popup_Padding, 1.5rem);
    transition: var(--Popup_Duration, 200ms) opacity;
    width: var(--Popup_Width, 28rem);
`;
const AbstractPopupInner = styled.div `
    --Popup_DefaultOffsetTransition: var(--Popup_Duration, 200ms);

    transform: translateX(var(--Popup_InitialOffsetX, 0))
        translateY(var(--Popup_InitialOffsetY, 0))
        scale(var(--Popup_InitialScale, 1));
    transition: var(
        --Popup_OffsetTransition,
        var(--Popup_DefaultOffsetTransition)
    );
    transition-delay: var(--Popup_Duration, 200ms), 0s;
    transition-property: visibility, transform;
    visibility: hidden;
`;
const AbstractPopupOuter = styled.div `
    position: absolute;
    left: var(--Popup_Left, 0);
    top: var(--Popup_Top, 0);
    transform: translateX(var(--Popup_X, var(--Popup_AnchorX, 0)))
        translateY(var(--Popup_Y, var(--Popup_AnchorY, 0)));
    overflow: visible;
    width: var(--Popup_OuterWidth, auto);
    height: var(--Popup_OuterHeight, auto);
`;
const AbstractPopupBackdrop = styled.div `
    background-color: var(--Popup_InitialBackdropColor, none);
    inset: 0;
    pointer-events: none;
    position: var(--Popup_BackdropPosition, fixed);
    transition: var(--Popup_Duration, 200ms) background-color;
    user-select: none;

    ${({ $phase }) => $phase === 'mounted' &&
    css `
            background-color: var(--Popup_BackdropColor, rgba(0, 0, 0, 0.2));
            pointer-events: all;

            ${AbstractPopupInner} {
                transform: translateX(
                        var(--Popup_OffsetX, var(--Popup_InitialOffsetX, 0))
                    )
                    translateY(
                        var(--Popup_OffsetY, var(--Popup_InitialOffsetY, 0))
                    )
                    scale(var(--Popup_Scale, 1));
                transition-delay: 0s;
                visibility: visible;
            }

            ${AbstractPopupContent} {
                opacity: var(--Popup_Opacity, 1);
            }
        `}
`;
export const DismissalReason = {
    CloseButton: Symbol('CloseButton'),
    Backdrop: Symbol('Backdrop'),
    Escape: Symbol('Escape'),
    Abandon: Symbol('Abandon'),
};
